import { Country } from '@xemplo/country-helper';
import { createListItems, GroupBuilder } from '@xemplo/stateful-list-core';

import { getCountryFlag } from '../flags/flags.helper';
import { NormalizedFlagsProps, RuntimeFlagMode } from '../phone-field.types';

function splitHighlightedCountries(
  countryInfo: Country.CountryInfo[],
  highlights: Country.Code[],
) {
  const highlightedSet = new Set(highlights);
  const highlighted: Country.CountryInfo[] = [];
  const others: Country.CountryInfo[] = [];

  countryInfo.forEach((info) => {
    if (highlightedSet.has(info.code)) {
      highlighted.push(info);
    } else {
      others.push(info);
    }
  });

  return { highlighted, others };
}

function itemsFromCountryInfo(
  group: GroupBuilder,
  countryInfo: Country.CountryInfo[],
  flagMode: RuntimeFlagMode,
) {
  countryInfo.forEach((info) => {
    const Icon = getCountryFlag(flagMode, info.code);
    group.addItem(info.code, `${info.name} (${info.phoneCode})`, undefined, Icon);
  });

  return group;
}

function makeListWithGroupedCountries(
  countryInfo: Country.CountryInfo[],
  highlights: Country.CountryInfo[],
  flagMode: RuntimeFlagMode,
) {
  return createListItems()
    .addGroup((group) => itemsFromCountryInfo(group, highlights, flagMode))
    .addGroup((group) => {
      group.addHeader('All Countries');
      return itemsFromCountryInfo(group, countryInfo, flagMode);
    })
    .build();
}

function makeListWithoutGroupedCountries(
  countryInfo: Country.CountryInfo[],
  flagMode: RuntimeFlagMode,
) {
  return createListItems()
    .addGroup((group) => itemsFromCountryInfo(group, countryInfo, flagMode))
    .build();
}

/**
 * Retrieves a list of country information based on the provided selectable countries.
 * If no selectable countries are specified, returns all available countries.
 * Otherwise, maps the selectable countries to their corresponding country info and filters out any undefined entries.
 *
 * @param params - An object containing the selectable countries.
 * @param params.selectableCountries - An optional array of country identifiers to filter the list.
 * @returns An array of {@link Country.CountryInfo} objects representing the countries.
 */
function getCountriesList({
  selectableCountries,
}: Omit<NormalizedFlagsProps, 'useSprite'>) {
  if (!selectableCountries) return Country.getAllCountries();

  return selectableCountries
    .map(Country.getCountryInfo)
    .filter((info) => info !== undefined) as Country.CountryInfo[];
}

/**
 * Generates a list of country items for a dropdown or similar component.
 * Based on UX requirements, if we are using all countries, we will group
 * the countries, having either the highlighted country at the top, or
 * the default country if no highlighted country is set.
 *
 * @param countryInfo - An array of country information objects containing details like name, code, and phone code.
 * @param flagMode - The mode for displaying country flags (e.g., runtime flag mode).
 * @returns A built list of country items, each including the country flag, name, and phone code.
 */
export function makeCountriesList(props: Omit<NormalizedFlagsProps, 'useSprite'>) {
  const { flagMode, defaultCountryCode, highlights } = props;
  const shouldGroup = props.selectableCountries == null;
  const countryInfo = getCountriesList(props);

  if (!shouldGroup) return makeListWithoutGroupedCountries(countryInfo, flagMode);

  const toHighlight = highlights?.length ? highlights : [defaultCountryCode];
  const { highlighted, others } = splitHighlightedCountries(countryInfo, toHighlight);

  return makeListWithGroupedCountries(others, highlighted, flagMode);
}
